/**
 * Purpose:
 *   Presentational blank state with illustration and message for reusable UI.
 *
 * Notes:
 *   - Expects caller-provided i18n text (e.g., subscription namespace)
 *   - Uses public illustrations keyed by the blank state type
 */

import { cx } from '@/util/functions/cx';
import { Button } from '@/components/ui/button/Button';
import type { TButtonSize, TButtonVariant } from '@/components/ui/button/Button.types';
import { blankStateStyles } from './BlankState.styles';

export type TBlankStateType = 'not-found' | 'not-match' | 'error-404';

export type TBlankStateVariant = 'default' | 'compact';

export interface IBlankStateAction {
  label: string;
  onClick: () => void;
  dataTestId?: string;
  variant?: TButtonVariant;
  size?: TButtonSize;
  disabled?: boolean;
}

export interface IBlankStateProps {
  type: TBlankStateType;
  message?: string;
  /** Use "compact" for table empty state (smaller illustration and spacing). */
  variant?: TBlankStateVariant;
  className?: string;
  action?: IBlankStateAction;
}

export function BlankState({
  type,
  message,
  variant = 'default',
  className,
  action,
}: IBlankStateProps) {
  const isCompact = variant === 'compact';
  const illustrationSize = isCompact ? 96 : 180;
  const illustrationByType: Record<TBlankStateType, string> = {
    'not-found': '/illustrations/not-found.svg',
    'not-match': '/illustrations/not-match.svg',
    'error-404': '/illustrations/error-404.svg',
  };

  return (
    <div
      className={cx(
        isCompact
          ? blankStateStyles.containerCompact
          : blankStateStyles.container,
        className
      )}
      role="status"
      aria-live="polite"
    >
      <img
        src={illustrationByType[type]}
        alt={message ?? ''}
        width={illustrationSize}
        height={illustrationSize}
        className={
          isCompact
            ? blankStateStyles.illustrationCompact
            : blankStateStyles.illustration
        }
        loading="eager"
        decoding="async"
      />
      {message && (
        <p
          className={
            isCompact
              ? blankStateStyles.messageCompact
              : blankStateStyles.message
          }
        >
          {message}
        </p>
      )}
      {action ? (
        <div
          className={
            isCompact
              ? blankStateStyles.actionCompact
              : blankStateStyles.action
          }
        >
          <Button
            type="button"
            variant={action.variant ?? 'secondary'}
            size={action.size ?? (isCompact ? 'sm' : 'md')}
            onClick={action.onClick}
            dataTestId={action.dataTestId}
            disabled={action.disabled}
          >
            {action.label}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
